import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@shared/api/client';
import { Breadcrumb } from '@shared/chrome/Breadcrumb';
import { resolveErrorCopy } from '@shared/errors/catalog';
import { formatNumber } from '@shared/format';
import { ConfirmDialog, type ConfirmSubject } from '@shared/ui/dialog';
import {
  DTable,
  DTableBody,
  DTableHead,
  DTableRowHeader,
  DTableScroll,
  RowSkeleton,
} from '@shared/ui/dtable';
import { EmptyState } from '@shared/ui/EmptyState';
import { ErrorBanner } from '@shared/ui/ErrorBanner';
import { EditIcon, PlusIcon, TrashIcon } from '@shared/ui/icons';
import { RowMenu, type RowMenuEntry } from '@shared/ui/rowmenu';

import { tagErrors } from '../_shared/errors';
import { ROOT_PARENT_VALUE } from '../_shared/forms.schema';
import { TermDialog } from '../_shared/TermDialog';
import type { TagTerm, TermDeletion, TermDraft } from '../_shared/types';
import { copy } from './copy';
import { useCreateTag, useDeleteTag, useUpdateTag } from './mutations';
import { tagsKey, useTags } from './queries';

const TABLE_COLUMNS = 2;

/**
 * Tag-vocabulary management — the flat many-to-many labels of the catalogue,
 * with every destructive action confirmed by name.
 *
 * **This page is a table where its sibling is a tree, and the difference is the
 * feature.** A tag is flat in as many words: no parent, no child, no path.
 * Drawing it in the tree component would offer a disclosure triangle on a record
 * that can never have children, and drawing the categories in a table would lose
 * the depth the catalogue grants them.
 *
 * **Two columns, and the thinness is the honest answer.** The name and the row
 * menu are what the row supports. A usage count, a created date, a colour, or a
 * public-listing link would each be a column with no row behind it — and a
 * two-column table carrying the actions the catalogue does grant is worth more
 * than a wide one carrying invented data. It takes **no minimum inline size**,
 * unlike every other table in this console: that floor exists because
 * five-column tables crush at 320 px, and two columns beside a 3.5 rem handle
 * leave the name the rest of the row without the wrapper ever scrolling.
 *
 * **The dialog is the sibling's dialog with one field fewer**, and the parent
 * control is *absent* rather than disabled: a permanently dead field would have
 * to explain its own presence, and the context line points an operator who
 * wanted the tree at the page that has one.
 *
 * Deliberately absent, each for a stated reason: **no sort, filter, or search
 * over the vocabulary** — those controls belong to the subscriber inbox and to
 * public search, and borrowing one across surfaces invents configuration; **no
 * pager**, because the catalogue grants one to the inbox alone; **no link to the
 * public tag listing**, because this area's outbound set is fixed at the
 * footer's two entries.
 */
export function TagsPage() {
  const query = useTags();
  const queryClient = useQueryClient();

  const createTag = useCreateTag();
  const updateTag = useUpdateTag();
  const deleteTag = useDeleteTag();

  const [draft, setDraft] = useState<TermDraft | null>(null);
  const [deletion, setDeletion] = useState<TermDeletion | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [writeError, setWriteError] = useState<unknown>(null);

  /** Where focus lands after a row leaves the table — see the tree's twin. */
  const addButtonRef = useRef<HTMLButtonElement>(null);

  const rows = query.data?.items ?? [];
  const total = query.data?.total ?? rows.length;

  function openCreate() {
    setWriteError(null);
    setDraft({ mode: 'create-root', termId: null, name: '', parentId: null, forbiddenParentIds: [] });
  }

  function openEdit(tag: TagTerm) {
    setWriteError(null);
    setDraft({ mode: 'edit', termId: tag.id, name: tag.name, parentId: null, forbiddenParentIds: [] });
  }

  function submitDraft(values: { name: string }) {
    if (!draft) return;
    setWriteError(null);

    const settle = (announce: string) => {
      setDraft(null);
      setAnnouncement(announce);
    };

    if (draft.mode === 'edit' && draft.termId) {
      updateTag.mutate(
        { tagId: draft.termId, name: values.name },
        { onSuccess: () => settle(copy.announce.updated(values.name)), onError: setWriteError },
      );
      return;
    }

    createTag.mutate(
      { name: values.name },
      { onSuccess: () => settle(copy.announce.created(values.name)), onError: setWriteError },
    );
  }

  function confirmDeletion() {
    if (!deletion) return;
    const { termId, name } = deletion;
    setWriteError(null);

    deleteTag.mutate(termId, {
      onSuccess: () => {
        setAnnouncement(copy.announce.deleted(name));
        addButtonRef.current?.focus();
      },
      onError: setWriteError,
    });

    setDeletion(null);
  }

  const confirmSubject: ConfirmSubject | null = deletion
    ? {
        heading: copy.confirm.heading(deletion.name),
        body: copy.confirm.body,
        commitLabel: copy.confirm.commit,
        intent: 'danger',
      }
    : null;

  return (
    <>
      <Breadcrumb items={[{ label: copy.crumbs.root, to: '/' }, { label: copy.crumbs.current }]} />

      <div className="mb-6 mt-3 grid gap-stack-tight">
        <h1 className="font-display text-h1 font-bold">{copy.head.title}</h1>
        <p className="mb-0 max-w-measure text-[1.0625rem] text-ink-muted">{copy.head.lede}</p>
      </div>

      <p role="status" aria-live="polite" className="sr-only">
        {announcement}
      </p>

      <section aria-labelledby="taxonomy-tags">
        <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2">
          <h2 id="taxonomy-tags" className="font-display text-h2 font-bold">
            {copy.section.heading}
          </h2>
          <button
            ref={addButtonRef}
            type="button"
            onClick={openCreate}
            className="ms-auto flex min-h-6 items-center gap-2 rounded-sm border border-border-strong bg-surface-raised px-3 text-xs font-semibold text-ink hover:bg-surface-overlay"
          >
            <PlusIcon className="flex-none" />
            <span>{copy.section.add}</span>
          </button>
        </div>

        <p className="mb-4 mt-3 max-w-measure text-xs text-ink-muted">{copy.section.context}</p>

        {writeError ? (
          <div className="mb-4">
            <ErrorBanner {...resolve(writeError)} />
          </div>
        ) : null}

        {query.isError ? (
          <ErrorBanner
            {...resolve(query.error)}
            onRetry={() => void queryClient.invalidateQueries({ queryKey: tagsKey() })}
          />
        ) : null}

        {query.isPending ? (
          <>
            <p role="status" className="sr-only">
              {copy.states.loading}
            </p>
            <DTableScroll>
              <DTable caption={copy.section.caption} minInlineSize="0">
                <TagTableHead />
                <DTableBody>
                  <RowSkeleton columns={TABLE_COLUMNS} />
                </DTableBody>
              </DTable>
            </DTableScroll>
          </>
        ) : null}

        {query.data ? (
          rows.length === 0 ? (
            <EmptyState message={copy.states.empty} />
          ) : (
            <>
              <DTableScroll>
                <DTable caption={copy.section.caption} minInlineSize="0">
                  <TagTableHead />
                  <DTableBody>
                    {rows.map((tag) => (
                      <tr key={tag.id}>
                        <DTableRowHeader className="font-medium">{tag.name}</DTableRowHeader>
                        <td className="whitespace-nowrap [inline-size:3.5rem]">
                          <RowMenu label={copy.menu.label(tag.name)} entries={menuEntries(tag)} />
                        </td>
                      </tr>
                    ))}
                  </DTableBody>
                </DTable>
              </DTableScroll>

              {/*
                Not an empty state and not a pager: a list with no traversal
                control states how many of how many it is showing, always.
              */}
              <p className="mb-0 mt-3 max-w-measure text-xs text-ink-muted">
                {copy.truncation(formatNumber(rows.length), formatNumber(total))}
              </p>
            </>
          )
        ) : null}
      </section>

      <TermDialog
        open={draft !== null}
        title={draft?.mode === 'edit' ? copy.dialog.titleEdit : copy.dialog.titleCreate}
        context={copy.dialog.context}
        nameLabel={copy.dialog.nameLabel}
        nameHint={copy.dialog.nameHint}
        saveLabel={copy.dialog.save}
        messages={copy.validation}
        defaultName={draft?.name ?? ''}
        defaultParentId={ROOT_PARENT_VALUE}
        // Absent, never disabled — the whole difference between the two surfaces.
        parent={null}
        busy={createTag.isPending || updateTag.isPending}
        onSubmit={submitDraft}
        onClose={() => setDraft(null)}
      />

      <ConfirmDialog
        open={deletion !== null}
        subject={confirmSubject}
        busy={deleteTag.isPending}
        onConfirm={confirmDeletion}
        onCancel={() => setDeletion(null)}
      />
    </>
  );

  function menuEntries(tag: TagTerm): RowMenuEntry[] {
    return [
      {
        key: `${tag.id}:edit`,
        label: copy.menu.edit,
        icon: <EditIcon className="flex-none" />,
        onSelect: () => openEdit(tag),
      },
      {
        key: `${tag.id}:delete`,
        label: copy.menu.delete,
        icon: <TrashIcon className="flex-none" />,
        danger: true,
        onSelect: () => setDeletion({ termId: tag.id, name: tag.name }),
      },
    ];
  }
}

/** The head is written once and read by the skeleton and the data body alike. */
function TagTableHead() {
  return (
    <DTableHead>
      <tr>
        <th scope="col">{copy.columns.name}</th>
        <th scope="col" className="[inline-size:3.5rem]">
          <span className="sr-only">{copy.columns.menu}</span>
        </th>
      </tr>
    </DTableHead>
  );
}

function resolve(error: unknown) {
  const status = error instanceof ApiError ? error.status : 0;
  const code = error instanceof ApiError ? error.code : undefined;
  const resolved = resolveErrorCopy(code, status, tagErrors);

  return {
    message: resolved.message,
    tone: resolved.tone === 'danger' ? ('danger' as const) : ('neutral' as const),
  };
}
